var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from "react";
// mui
import { Stack, lighten, Collapse, Box, } from "@mui/material";
import LabelIcon from "@mui/icons-material/LabelOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
//components
import { Select, Text, CircularProgress, Tooltip } from "../../../../Atoms";
import { ActionIcon } from "../../Atoms";
import TablePaginationActions from "./TablePaginationActions";
import SearchBar from "../../Molecules/SearchBar";
// constants
import { COLORS } from "../../../../../constants";
// hooks
import { useSearchContext } from "../../searchContext";
import { useScreenSize } from "../../../../../hooks";
import { nFormatter } from "../../../../../helpers";
import { TooltipInfoIcon } from "../../../../Molecules";
var TableToolbar = function (_a) {
    var paginateProps = _a.paginateProps, downloadProps = _a.downloadProps, tagProps = _a.tagProps, title = _a.title, titleTooltipProps = _a.titleTooltipProps, titleInfoIconTooltip = _a.titleInfoIconTooltip, subTitle = _a.subTitle, _b = _a.subTitleVariant, subTitleVariant = _b === void 0 ? "body2" : _b, toolbarInside = _a.toolbarInside, selectedTitle = _a.selectedTitle, inDrawer = _a.inDrawer, position = _a.position, showBorder = _a.showBorder, _c = _a.titleVariant, titleVariant = _c === void 0 ? "subtitle1" : _c, toolbarCollapse = _a.toolbarCollapse, numChecked = _a.numChecked, searchBarProps = _a.searchBarProps, _d = _a.isLoading, isLoading = _d === void 0 ? false : _d, bulkActions = _a.bulkActions;
    var searchOpen = useSearchContext().searchOpen;
    var _e = __read(useState(false), 2), collapseOpen = _e[0], setCollapseOpen = _e[1];
    var isMedium = useScreenSize().isMedium;
    var toggleCollapse = useCallback(function () { return setCollapseOpen(function (c) { return !c; }); }, []);
    var labelDisplayedRows = useCallback(function (page, rowsPerPage, count) {
        var from = (page - 1) * rowsPerPage + 1;
        var to = page * rowsPerPage;
        return "".concat(from, "-").concat(to > count ? count : to, " of ").concat(nFormatter(count, true));
    }, []);
    //Hide title if in drawer or lower than medium screensize when search is open
    var isLargeToolbar = !searchOpen || (isMedium && !inDrawer);
    /**
     * triggers when page changes
     */
    var handleChangePage = function (_, newPage) {
        paginateProps === null || paginateProps === void 0 ? void 0 : paginateProps.onChangePage(newPage, position);
    };
    /**
     * triggers when dropdown changes for rows per page
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var handleChangeRowsPerPage = function (event) {
        paginateProps === null || paginateProps === void 0 ? void 0 : paginateProps.onChangeRows(parseInt(event.target.value, 10), position);
    };
    var collapseIcon = useMemo(function () {
        if (toolbarCollapse) {
            var collapseContent = toolbarCollapse.collapseContent, isFlat = toolbarCollapse.isFlat, Icon = toolbarCollapse.Icon;
            if (isFlat) {
                return collapseContent;
            }
            return (_jsx(ActionIcon, __assign({ selected: !!collapseOpen, "data-testid": "toolbar".concat(position, "-filter-icon"), active: true, action: toggleCollapse }, { children: _jsx(Icon, { fontSize: "small" }) })));
        }
        return null;
    }, [collapseOpen]);
    /**
     * Download icon
     */
    var downloadIcon = useMemo(function () {
        if (downloadProps) {
            return (_jsx(ActionIcon, __assign({ "data-testid": "toolbar".concat(position, "-download-icon") }, downloadProps, { children: _jsx(SaveAltIcon, { fontSize: "small" }) })));
        }
        return null;
    }, [downloadProps]);
    /**
     * Tagging Icon
     */
    var tagIcon = useMemo(function () {
        if (tagProps) {
            return (_jsx(ActionIcon, __assign({ "data-testid": "toolbar".concat(position, "-tag-icon") }, tagProps, { sx: { ml: 1 } }, { children: _jsx(LabelIcon, { fontSize: "small" }) })));
        }
        return null;
    }, [tagProps]);
    var chipProps = useMemo(function () { return (__assign({ variant: "secondary", selectable: false }, paginateProps === null || paginateProps === void 0 ? void 0 : paginateProps.chipProps)); }, []);
    /**
     * Pagination
     * dropdown, first item, last item, next item, last item
     */
    var renderPagination = function () {
        if (!paginateProps) {
            return null;
        }
        var page = paginateProps.page, rowsPerPage = paginateProps.rowsPerPage, count = paginateProps.count, rowOptions = paginateProps.rowOptions;
        return (_jsx(React.Fragment, { children: _jsxs(Stack, __assign({ direction: "row", alignItems: "center" }, { children: [_jsx(Select, { onChange: handleChangeRowsPerPage, asChip: true, chipProps: chipProps, value: rowsPerPage, menuTitle: "Show rows", name: "rows-".concat(position), customDisplayValue: labelDisplayedRows(page, rowsPerPage, count), options: rowOptions }), _jsx(TablePaginationActions, { count: count, position: position, onPageChange: handleChangePage, page: page, inDrawer: inDrawer, toolbarInside: toolbarInside, rowsPerPage: rowsPerPage })] })) }));
    };
    /**
     * Display number of selected results if any are selected
     */
    var selectedCount = useMemo(function () {
        return numChecked && selectedTitle ? (_jsxs(Text, __assign({ variant: "overline", emphasis: "medium", sx: { ml: 5, lineHeight: "1.3rem", alignSelf: "flex-end" } }, { children: [numChecked, " ", selectedTitle, " Selected"] }))) : null;
    }, [numChecked, selectedTitle]);
    /**
     * Dont render toolbar if no toolbar props exist
     */
    if (!paginateProps && !downloadProps && !tagProps && !title) {
        return null;
    }
    return (_jsxs(Stack, { children: [_jsxs(Stack, __assign({ pr: toolbarInside ? 0 : 6, pb: toolbarInside ? 2.5 : 4, direction: "row", sx: {
                    borderTop: showBorder
                        ? "1px solid ".concat(lighten(COLORS.BLACK, 0.9))
                        : "none",
                }, justifyContent: "flex-end", alignItems: "center" }, { children: [isLargeToolbar && (title || numChecked) ? (_jsxs(Stack, __assign({ direction: "row", alignItems: "center", gap: 1, sx: { mr: "auto" } }, { children: [_jsx(Tooltip, __assign({ wrap: false }, titleTooltipProps, { title: (titleTooltipProps === null || titleTooltipProps === void 0 ? void 0 : titleTooltipProps.title) || "" }, { children: _jsxs(Stack, __assign({ direction: "row" }, { children: [_jsx(Text, __assign({ variant: titleVariant, loadingProps: { sx: { width: "320px" } } }, { children: isLoading ? undefined : title })), selectedCount] })) })), titleInfoIconTooltip && (_jsx(TooltipInfoIcon, { title: titleInfoIconTooltip, iconColor: "black" }))] }))) : null, isLoading ? (_jsx(CircularProgress, { size: "20px", sx: { color: COLORS.P_OUTLINE } })) : (_jsxs(Stack, __assign({ direction: "row", alignItems: "center", gap: inDrawer ? 0 : 2 }, { children: [_jsxs(Stack, __assign({ direction: "row", alignItems: "center", gap: 2 }, { children: [bulkActions, searchBarProps && (_jsx(SearchBar, __assign({}, searchBarProps, { isLargeToolbar: isLargeToolbar }))), collapseIcon, downloadIcon, tagIcon] })), renderPagination()] })))] })), subTitle && _jsx(Text, __assign({ variant: subTitleVariant }, { children: subTitle })), toolbarCollapse && (_jsx(Collapse, __assign({ in: collapseOpen, timeout: "auto", "data-testid": "collapse-filters" }, { children: _jsx(Box, __assign({ pr: 5, pb: 4 }, { children: toolbarCollapse === null || toolbarCollapse === void 0 ? void 0 : toolbarCollapse.collapseContent })) })))] }));
};
export default TableToolbar;
